import { Accordion } from '@positivote/design-system/components/Accordion'
import { Button } from '@positivote/design-system/components/Button'
import { Div } from '@positivote/design-system/components/Div'
import { Divider } from '@positivote/design-system/components/Divider'
import { Typography } from '@positivote/design-system/components/Typography'
import { AlertErrorIcon } from '@positivote/design-system/icons/AlertError'
import { CheckCircleIcon } from '@positivote/design-system/icons/CheckCircle'
import { PlayCircleIcon } from '@positivote/design-system/icons/PlayCircle'
import { useEffect, useState } from 'react'

import { i18n } from '@/common/i18n'

export interface AccordionAudioProps {
  urlAudio: string | undefined
  onChangeAudioResult: (result: string) => void
}

export function AccordionAudio({
  urlAudio,
  onChangeAudioResult
}: AccordionAudioProps): JSX.Element {
  const [playerAudioProps, setPlayerAudioPros] = useState({
    isVisible: false,
    testResult: ''
  })

  useEffect(() => {
    onChangeAudioResult(playerAudioProps.testResult)
    // DOCS: This useeffect can only be used when changing states
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerAudioProps.testResult])

  return (
    <Accordion
      isOpen={true}
      Summary={
        <Typography variant="titleMedium">
          {i18n().modules.requirements.components.accordionAudio.title}
        </Typography>
      }
      css={{
        borderRadius: '$lg',
        backgroundColor: '$surface-2',
        overflow: 'hidden',
        '& .Accordion-Details': {
          backgroundColor: '$background'
        }
      }}
    >
      <Divider />
      <Div
        css={{
          display: 'flex',
          gap: '$md',
          justifyContent: 'space-between',
          padding: '$md $lg $md $3xl',
          alignItems: 'center',
          position: 'relative',
          '@xs': {
            flexDirection: 'column',
            alignItems: 'start'
          }
        }}
      >
        {!playerAudioProps.isVisible && !playerAudioProps.testResult.length && (
          <>
            <Typography variant="bodyMedium" css={{ color: '$on-surface-variant' }}>
              {i18n().modules.requirements.components.accordionAudio.audioTest}
            </Typography>
            <Button
              LeadingIcon={<PlayCircleIcon />}
              onClick={() => setPlayerAudioPros((oldState) => ({ ...oldState, isVisible: true }))}
            >
              {i18n().modules.requirements.components.accordionAudio.buttons.executeTest}
            </Button>
          </>
        )}
        {playerAudioProps.isVisible && (
          <Div css={{ display: 'flex', width: '100%', gap: '$md', flexDirection: 'column' }}>
            <audio
              src={urlAudio}
              controls
              autoPlay
              style={{ width: '100%', borderRadius: '8px' }}
            ></audio>
            <Div
              css={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Typography variant="bodyMedium" css={{ color: '$on-surface-variant' }}>
                {i18n().modules.requirements.components.accordionAudio.questionAudio}
              </Typography>
              <Div css={{ display: 'flex', gap: '$md' }}>
                <Button
                  variant="outlined"
                  onClick={() => setPlayerAudioPros({ isVisible: false, testResult: 'fail' })}
                >
                  {i18n().modules.requirements.components.accordionAudio.buttons.no}
                </Button>
                <Button
                  onClick={() => setPlayerAudioPros({ isVisible: false, testResult: 'pass' })}
                >
                  {i18n().modules.requirements.components.accordionAudio.buttons.yes}
                </Button>
              </Div>
            </Div>
          </Div>
        )}
        {!!playerAudioProps.testResult.length && (
          <Div
            css={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }}
          >
            <Button
              variant="outlined"
              onClick={() =>
                setPlayerAudioPros({
                  isVisible: true,
                  testResult: ''
                })
              }
            >
              {i18n().modules.requirements.components.accordionAudio.buttons.testAgain}
            </Button>
            {playerAudioProps.testResult === 'pass' ? (
              <CheckCircleIcon fill="$success" />
            ) : (
              <AlertErrorIcon fill="$critical" />
            )}
          </Div>
        )}
      </Div>
    </Accordion>
  )
}
